// src/hooks/useNotificacoes.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const NOTIF_KEY = 'notificacoes';

export const useNotificacoes = (params = {}) => {
    return useQuery({
        queryKey: [NOTIF_KEY, 'lista', params],
        queryFn: async () => {
            const { data } = await apiClient.get('/notificacoes', { params });
            return {
                data: data.data || [],
                count: data.count || data.totalCount || 0,
                pagination: data.pagination || {},
            };
        },
        staleTime: 1000 * 30,
    });
};

export const useContadorNaoLidas = () => {
    return useQuery({
        queryKey: [NOTIF_KEY, 'contador'],
        queryFn: async () => {
            const { data } = await apiClient.get('/notificacoes/nao-lidas/contagem');
            return data.data?.count ?? 0;
        },
        staleTime: 1000 * 30,
        refetchInterval: 1000 * 60,
    });
};

export const useEstatisticasNotificacoes = () => {
    return useQuery({
        queryKey: [NOTIF_KEY, 'estatisticas'],
        queryFn: async () => {
            const { data } = await apiClient.get('/notificacoes/estatisticas');
            return data.data || {};
        },
        staleTime: 1000 * 60 * 5,
    });
};

export const usePreferenciasNotificacoes = () => {
    return useQuery({
        queryKey: [NOTIF_KEY, 'preferences'],
        queryFn: async () => {
            const { data } = await apiClient.get('/notificacoes/preferences');
            return data.data || data || {};
        },
        staleTime: 1000 * 60 * 10,
    });
};

export const useMutateNotificacoes = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: [NOTIF_KEY] });
    };

    const marcarLida = useMutation({
        mutationFn: (id) => apiClient.put(`/notificacoes/${id}/marcar-lida`),
        onSuccess: invalidate,
        onError: () => setAlert('Erro ao marcar notificação como lida.', 'danger'),
    });

    const marcarTodasLidas = useMutation({
        mutationFn: () => apiClient.put('/notificacoes/marcar-todas-lidas'),
        onSuccess: () => { invalidate(); setAlert('Todas as notificações foram marcadas como lidas.', 'success'); },
        onError: () => setAlert('Erro ao marcar todas como lidas.', 'danger'),
    });

    const deletarUma = useMutation({
        mutationFn: (id) => apiClient.delete(`/notificacoes/${id}`),
        onSuccess: () => { invalidate(); setAlert('Notificação excluída.', 'success'); },
        onError: () => setAlert('Erro ao excluir notificação.', 'danger'),
    });

    const deletarTodas = useMutation({
        mutationFn: () => apiClient.delete('/notificacoes'),
        onSuccess: () => { invalidate(); setAlert('Todas as notificações foram excluídas.', 'success'); },
        onError: () => setAlert('Erro ao excluir notificações.', 'danger'),
    });

    const enviarMultiplas = useMutation({
        mutationFn: (payload) => apiClient.post('/notificacoes/multiplas', payload),
        onSuccess: () => setAlert('Notificação enviada com sucesso!', 'success'),
        onError: (err) => setAlert(err.response?.data?.error || 'Erro ao enviar notificação.', 'danger'),
    });

    const salvarPreferencias = useMutation({
        mutationFn: (prefs) => apiClient.put('/notificacoes/preferences', prefs),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [NOTIF_KEY, 'preferences'] });
            setAlert('Preferências salvas com sucesso!', 'success');
        },
        onError: () => setAlert('Erro ao salvar preferências.', 'danger'),
    });

    return {
        marcarLida,
        marcarTodasLidas,
        deletarUma,
        deletarTodas,
        enviarMultiplas,
        salvarPreferencias,
    };
};
